import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { selectUserToken } from '../../../redux/features/user/userSelectors';
import { useGetParserStatsQuery } from '../../../redux/services/parser/parserApi';
import { Loader } from '../Loader';
import { SessionsHistoryModal } from './SessionsHistoryModal';

const COLORS = {
  red: '#e84855',
  orange: '#fa913c',
  yellow: '#fabd23',
  green: '#9bc53d',
  greenMint: '#66d48a',
  blue: '#52e3e1',
  bluePastel: '#bbe5fc',
  darkBlue: '#33a8c7',
  purple: '#848cba',
  gray: '#6b7280',
};

const fillTierColor = pct => {
  if (pct >= 95) return COLORS.green;
  if (pct >= 85) return COLORS.yellow;
  if (pct >= 50) return COLORS.orange;
  return COLORS.red;
};

const eventColor = event => {
  if (event === 'Added') return COLORS.green;
  if (event === 'Changed') return COLORS.blue;
  if (event === 'Unavailable') return COLORS.orange;
  return COLORS.red;
};

const sessionStatusColor = status => {
  if (status === 'Completed') return COLORS.green;
  if (status === 'Error') return COLORS.red;
  if (status === 'In Progress') return COLORS.blue;
  return COLORS.gray;
};

const formatDuration = seconds => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
};

const formatTimestamp = ts => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
};

const deriveRunStatus = latest => {
  if (!latest) return { label: 'No runs yet', color: COLORS.gray, tone: 'default' };
  const completed = latest.tasks_completed ?? 0;
  const errored = latest.tasks_error ?? 0;
  const inProgress = latest.tasks_in_progress ?? 0;
  const total = completed + errored + inProgress;
  if (inProgress > 0) return { label: 'Running', color: COLORS.blue, tone: 'default' };
  if (total === 0) return { label: latest.status || 'Unknown', color: COLORS.gray, tone: 'default' };
  if (errored === 0) return { label: 'Completed', color: COLORS.green, tone: 'success' };
  if (completed === 0) return { label: 'Failed', color: COLORS.red, tone: 'error' };
  return { label: 'Completed with errors', color: COLORS.orange, tone: 'warn' };
};

const LatestRunPanel = ({ latest, onViewHistory }) => {
  if (!latest) {
    return (
      <div className="w-full p-4 mb-4 bg-white border border-gray-200 rounded shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm text-darkGrayColor">No parser runs yet.</div>
          <button
            type="button"
            data-testid="parser-stats-history-button"
            onClick={onViewHistory}
            className="text-xs text-purpleColor hover:underline"
          >
            View history →
          </button>
        </div>
      </div>
    );
  }

  const { label, color } = deriveRunStatus(latest);
  const completed = latest.tasks_completed ?? 0;
  const errored = latest.tasks_error ?? 0;
  const inProgress = latest.tasks_in_progress ?? 0;
  const total = latest.tasks_total ?? completed + errored + inProgress;
  const pctCompleted = total > 0 ? (completed / total) * 100 : 0;
  const pctErrored = total > 0 ? (errored / total) * 100 : 0;
  const pctInProgress = total > 0 ? (inProgress / total) * 100 : 0;
  const erroredSpiders = latest.errored_spiders ?? [];

  return (
    <div
      className="w-full p-5 mb-4 bg-white border border-gray-200 border-l-4 rounded shadow-sm"
      style={{ borderLeftColor: color }}
    >
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="text-xs uppercase tracking-wide text-darkGrayColor">Latest run</span>
        <span className="text-lg font-semibold text-blackColor">Session #{latest.id}</span>
        <span
          className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full"
          style={{ backgroundColor: color + '22', color }}
        >
          {label}
        </span>
        {latest.status && latest.status !== label && (
          <span
            className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider rounded bg-lightGrayColor text-darkGrayColor"
            title="Raw session.status column value"
          >
            raw: {latest.status}
          </span>
        )}
        <button
          type="button"
          data-testid="parser-stats-history-button"
          onClick={onViewHistory}
          className="text-xs text-purpleColor hover:underline"
        >
          View history →
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wide text-darkGrayColor mb-1">Started</div>
          <div className="text-blackColor">{formatTimestamp(latest.created_at)}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-darkGrayColor mb-1">Finished</div>
          <div className="text-blackColor">
            {latest.finished_at ? formatTimestamp(latest.finished_at) : 'still running'}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-darkGrayColor mb-1">Duration</div>
          <div className="text-blackColor">{formatDuration(latest.duration_seconds)}</div>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-xs uppercase tracking-wide text-darkGrayColor">
            Spider tasks
          </span>
          <span className="text-xs text-darkGrayColor">
            {completed} of {total} completed
          </span>
        </div>
        <div className="flex w-full h-2 overflow-hidden bg-lightGrayColor rounded">
          {pctCompleted > 0 && (
            <div style={{ width: `${pctCompleted}%`, backgroundColor: COLORS.green }} />
          )}
          {pctErrored > 0 && (
            <div style={{ width: `${pctErrored}%`, backgroundColor: COLORS.red }} />
          )}
          {pctInProgress > 0 && (
            <div style={{ width: `${pctInProgress}%`, backgroundColor: COLORS.blue }} />
          )}
        </div>
        <div className="flex flex-wrap gap-4 mt-2 text-xs">
          <span className="flex items-center gap-1.5 text-darkGrayColor">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.green }} />
            Completed {completed}
          </span>
          <span className="flex items-center gap-1.5 text-darkGrayColor">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.red }} />
            Error {errored}
          </span>
          <span className="flex items-center gap-1.5 text-darkGrayColor">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS.blue }} />
            In progress {inProgress}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex gap-6">
          <div>
            <div className="text-xs uppercase tracking-wide text-darkGrayColor mb-1">Adverts added</div>
            <div className="text-2xl font-semibold" style={{ color: COLORS.green }}>
              {(latest.adverts_added ?? 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-darkGrayColor mb-1">Adverts changed</div>
            <div className="text-2xl font-semibold" style={{ color: COLORS.darkBlue }}>
              {(latest.adverts_changed ?? 0).toLocaleString()}
            </div>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-darkGrayColor mb-1">
            Errored spiders {erroredSpiders.length > 0 && `(${erroredSpiders.length})`}
          </div>
          {erroredSpiders.length === 0 ? (
            <div className="text-sm text-darkGrayColor">none</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {erroredSpiders.map(s => (
                <span
                  key={s.source}
                  className="inline-flex items-center gap-1.5 px-2 py-1 text-xs bg-redColor/10 text-redColor border border-redColor/20 rounded"
                  style={{ backgroundColor: COLORS.red + '15', color: COLORS.red, borderColor: COLORS.red + '30' }}
                >
                  <span className="font-medium">{s.source}</span>
                  {s.duration_seconds != null && (
                    <span className="text-[10px] opacity-75">· {formatDuration(s.duration_seconds)}</span>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, sub, tone = 'default' }) => {
  const toneClass =
    tone === 'success'
      ? 'border-l-greenColor'
      : tone === 'error'
        ? 'border-l-redColor'
        : tone === 'warn'
          ? 'border-l-orangeColor'
          : 'border-l-darkBlueColor';
  return (
    <div className={`flex flex-col p-4 bg-white border border-gray-200 border-l-4 ${toneClass} rounded shadow-sm`}>
      <span className="text-xs uppercase tracking-wide text-darkGrayColor">{label}</span>
      <span className="mt-1 text-2xl font-semibold text-blackColor">{value}</span>
      {sub && <span className="mt-1 text-xs text-darkGrayColor">{sub}</span>}
    </div>
  );
};

const ChartCard = ({ title, subtitle, children, className = '' }) => (
  <div className={`flex flex-col p-4 bg-white border border-gray-200 rounded shadow-sm ${className}`}>
    <div className="mb-3">
      <h3 className="text-sm font-semibold text-blackColor">{title}</h3>
      {subtitle && <p className="mt-0.5 text-xs text-darkGrayColor">{subtitle}</p>}
    </div>
    <div className="flex-1 min-h-0">{children}</div>
  </div>
);

export const ParserStats = ({ source }) => {
  const userToken = useSelector(selectUserToken);
  const [collapsed, setCollapsed] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const { data, isLoading, error } = useGetParserStatsQuery({
    token: userToken,
    source: source || '',
  });

  const headerRow = (
    <div className="flex items-center justify-between w-full mb-3">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-blackColor">Parser stats</h2>
        {source && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-bluePastelColor text-blackColor rounded">
            source: <strong>{source}</strong>
          </span>
        )}
      </div>
      <button
        onClick={() => setCollapsed(prev => !prev)}
        className="flex items-center gap-1 px-3 py-1.5 text-xs text-darkGrayColor bg-white border border-gray-200 rounded hover:bg-lightGrayColor"
      >
        {collapsed ? (
          <>
            <ChevronDown size={14} /> Show
          </>
        ) : (
          <>
            <ChevronUp size={14} /> Hide
          </>
        )}
      </button>
    </div>
  );

  if (collapsed) {
    return <div className="w-full">{headerRow}</div>;
  }

  if (isLoading) {
    return (
      <div className="w-full">
        {headerRow}
        <div className="flex items-center justify-center p-8 bg-white border border-gray-200 rounded">
          <Loader />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="w-full">
        {headerRow}
        <div className="p-4 text-sm text-redColor bg-white border border-gray-200 rounded">
          Failed to load parser stats.
        </div>
      </div>
    );
  }

  const {
    overview = {},
    adverts_per_source = [],
    fill_rates = [],
    price_distribution = [],
    sessions_history = [],
    task_duration_by_spider = [],
    events_by_type = [],
  } = data;

  const latest = overview.latest_session;

  const fillRatesForChart = [...fill_rates].sort((a, b) => a.filled_pct - b.filled_pct);
  const advertsForChart = [...adverts_per_source].sort((a, b) => b.count - a.count);
  const priceForChart = price_distribution;
  const sessionsForChart = sessions_history.map(s => ({
    ...s,
    label: `#${s.id}`,
  }));
  const taskDurationsForChart = [...task_duration_by_spider].sort((a, b) => b.avg_seconds - a.avg_seconds);

  return (
    <div className="w-full">
      {headerRow}

      <LatestRunPanel latest={latest} onViewHistory={() => setHistoryOpen(true)} />
      <SessionsHistoryModal
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        token={userToken}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <KpiCard label="Total adverts" value={overview.total_adverts?.toLocaleString() ?? 0} />
        <KpiCard
          label="Sources producing"
          value={`${overview.sources_producing ?? 0} / ${overview.sources_attempted ?? 0}`}
          sub="spiders returning data / attempted"
        />
        <KpiCard label="Images" value={overview.total_images?.toLocaleString() ?? 0} />
        <KpiCard label="Features" value={overview.total_features?.toLocaleString() ?? 0} />
        <KpiCard
          label="Sessions total"
          value={overview.total_sessions ?? 0}
          sub={latest ? `last: ${formatTimestamp(latest.finished_at || latest.created_at)}` : undefined}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <ChartCard
          title="Adverts per source"
          subtitle="Total adverts persisted by each spider"
          className="h-80"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={advertsForChart} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" stroke="#6b7280" fontSize={11} tickFormatter={v => v.toLocaleString()} />
              <YAxis
                type="category"
                dataKey="source"
                stroke="#6b7280"
                fontSize={11}
                width={140}
                tick={{ fill: '#100C09' }}
              />
              <Tooltip
                formatter={value => value.toLocaleString()}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="count" fill={COLORS.darkBlue} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Field fill rates"
          subtitle="% of adverts with a non-NULL value. Red < 50%, orange < 85%, yellow < 95%, green ≥ 95%."
          className="h-80"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={fillRatesForChart} layout="vertical" margin={{ top: 5, right: 40, left: 100, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                type="number"
                stroke="#6b7280"
                fontSize={11}
                domain={[0, 100]}
                tickFormatter={v => `${v}%`}
              />
              <YAxis
                type="category"
                dataKey="field"
                stroke="#6b7280"
                fontSize={10}
                width={140}
                tick={{ fill: '#100C09' }}
              />
              <Tooltip
                formatter={(value, _name, entry) => [
                  `${value}% (${entry.payload.filled.toLocaleString()} / ${entry.payload.total.toLocaleString()})`,
                  'Fill rate',
                ]}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar
                dataKey="filled_pct"
                shape={props => (
                  <rect
                    x={props.x}
                    y={props.y}
                    width={props.width}
                    height={props.height}
                    fill={fillTierColor(props.payload.filled_pct)}
                  />
                )}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <ChartCard
          title="Price distribution"
          subtitle="Current price buckets (EUR). 'unknown' = NULL current_price."
          className="h-72"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={priceForChart} margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="bucket" stroke="#6b7280" fontSize={11} />
              <YAxis stroke="#6b7280" fontSize={11} tickFormatter={v => v.toLocaleString()} />
              <Tooltip
                formatter={value => value.toLocaleString()}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="count" fill={COLORS.greenMint} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Adverts touched per session"
          subtitle="Added (green, dot = session status), Changed (blue), Auto-imported (purple dashed) over the last 20 sessions."
          className="h-72"
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sessionsForChart} margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" stroke="#6b7280" fontSize={11} />
              <YAxis stroke="#6b7280" fontSize={11} tickFormatter={v => v.toLocaleString()} />
              <Tooltip
                formatter={(value, name) => [value.toLocaleString(), name]}
                labelFormatter={(label, payload) => {
                  const row = payload?.[0]?.payload;
                  if (!row) return label;
                  return `Session ${label} · ${row.status} · ${formatDuration(row.duration_seconds)}`;
                }}
                contentStyle={{ fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line
                type="monotone"
                name="Adverts added"
                dataKey="adverts_added"
                stroke={COLORS.darkBlue}
                strokeWidth={2}
                dot={props => {
                  const { cx, cy, payload } = props;
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={5}
                      fill={sessionStatusColor(payload.status)}
                      stroke="#fff"
                      strokeWidth={1.5}
                    />
                  );
                }}
                activeDot={{ r: 7 }}
              />
              <Line
                type="monotone"
                name="Adverts changed"
                dataKey="adverts_changed"
                stroke={COLORS.darkBlue}
                strokeWidth={2}
                dot={{ r: 3, fill: COLORS.darkBlue, stroke: '#fff', strokeWidth: 1 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                name="Auto-imported"
                dataKey="auto_imported"
                stroke={COLORS.purple}
                strokeWidth={2}
                strokeDasharray="4 3"
                dot={{ r: 3, fill: COLORS.purple, stroke: '#fff', strokeWidth: 1 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <ChartCard
          title="Task duration by spider"
          subtitle="Avg seconds per Completed run. Wider bars = slower spider (potential bottleneck)."
          className="lg:col-span-2 h-96"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={taskDurationsForChart}
              layout="vertical"
              margin={{ top: 5, right: 40, left: 60, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                type="number"
                stroke="#6b7280"
                fontSize={11}
                scale="log"
                domain={[0.1, 'dataMax']}
                tickFormatter={v => (v >= 60 ? `${Math.round(v / 60)}m` : `${v}s`)}
              />
              <YAxis
                type="category"
                dataKey="source"
                stroke="#6b7280"
                fontSize={10}
                width={140}
                tick={{ fill: '#100C09' }}
              />
              <Tooltip
                formatter={(value, _name, entry) => [
                  `avg ${formatDuration(value)} · max ${formatDuration(entry.payload.max_seconds)} (${entry.payload.runs} runs)`,
                  'Duration',
                ]}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar dataKey="avg_seconds" fill={COLORS.purple} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Events by type"
          subtitle="How the crawler classifies each URL touch."
          className="h-96"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={events_by_type} layout="vertical" margin={{ top: 5, right: 20, left: 40, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" stroke="#6b7280" fontSize={11} tickFormatter={v => v.toLocaleString()} />
              <YAxis
                type="category"
                dataKey="event"
                stroke="#6b7280"
                fontSize={11}
                width={130}
                tick={{ fill: '#100C09' }}
              />
              <Tooltip
                formatter={value => value.toLocaleString()}
                contentStyle={{ fontSize: 12 }}
              />
              <Bar
                dataKey="count"
                shape={props => (
                  <rect
                    x={props.x}
                    y={props.y}
                    width={props.width}
                    height={props.height}
                    fill={eventColor(props.payload.event)}
                  />
                )}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};
